import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  User as UserIcon,
  Calendar,
  Palmtree,
  Plus,
  Loader2,
  Mail,
  Briefcase,
  CreditCard,
  ShieldAlert,
  Save,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { InvoiceSettingsCard } from '../../components/portal/InvoiceSettingsCard';
import { PortalNotifications } from '../../components/portal/PortalNotifications';
import { MyHoursRequestsCard } from '../../components/portal/MyHoursRequestsCard';
import { InvoiceTemplateCard } from '../../components/portal/InvoiceTemplateCard';
import { SavedCustomersCard } from '../../components/portal/SavedCustomersCard';
import { format, parseISO, differenceInDays } from 'date-fns';

export function PortalProfile() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });

  // OA-1 §3 — contractor business + bank details
  const [contractorProfile, setContractorProfile] = useState(null);
  const [contractorSaving, setContractorSaving] = useState(false);
  const [contractorForm, setContractorForm] = useState({
    business_name: '',
    business_address: '',
    utr_number: '',
    ni_number: '',
    bank_account_name: '',
    bank_sort_code: '',
    bank_account_number: '',
    personal_email: '',
    business_phone: '',
  });
  // Track initial bank values so we can prompt if they change
  const [initialBank, setInitialBank] = useState({ bank_sort_code: '', bank_account_number: '', bank_account_name: '' });

  const isSubContractor = profile?.user_type === 'sub_contractor' || user?.user_type === 'sub_contractor';

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, requestsRes, contractorRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/vacation-requests').catch(() => ({ data: [] })),
        api.get('/portal/contractor-profile').catch(() => ({ data: null })),
      ]);
      setProfile(profileRes.data);
      setLeaveRequests(requestsRes.data || []);
      if (contractorRes.data) {
        setContractorProfile(contractorRes.data);
        setContractorForm({
          business_name: contractorRes.data.business_name || '',
          business_address: contractorRes.data.business_address || '',
          utr_number: contractorRes.data.utr_number || '',
          ni_number: contractorRes.data.ni_number || '',
          bank_account_name: contractorRes.data.bank_account_name || '',
          bank_sort_code: contractorRes.data.bank_sort_code || '',
          bank_account_number: contractorRes.data.bank_account_number || '',
          personal_email: contractorRes.data.personal_email || profileRes.data?.email || '',
          business_phone: contractorRes.data.business_phone || '',
        });
        setInitialBank({
          bank_sort_code: contractorRes.data.bank_sort_code || '',
          bank_account_number: contractorRes.data.bank_account_number || '',
          bank_account_name: contractorRes.data.bank_account_name || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveContractorProfile = async () => {
    const bankChanged = ['bank_account_name', 'bank_sort_code', 'bank_account_number'].some(
      k => (contractorForm[k] || '') !== (initialBank[k] || '')
    );
    if (bankChanged) {
      const ok = window.confirm(
        'You are about to change your bank details. Your next invoice will be flagged for admin review. Continue?'
      );
      if (!ok) return;
    }
    setContractorSaving(true);
    try {
      const res = await api.put('/portal/contractor-profile', contractorForm);
      setContractorProfile(res.data);
      setInitialBank({
        bank_sort_code: res.data.bank_sort_code || '',
        bank_account_number: res.data.bank_account_number || '',
        bank_account_name: res.data.bank_account_name || '',
      });
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      console.error(e);
    } finally {
      setContractorSaving(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/vacation-requests', {
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason || 'Holiday'
      });
      
      toast.success('Leave request submitted');
      setDialogOpen(false);
      setFormData({ start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'denied':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    }
  };

  const calculateDays = (start, end) => {
    return differenceInDays(parseISO(end), parseISO(start)) + 1;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  const approvedDays = leaveRequests
    .filter(r => r.status === 'approved')
    .reduce((sum, r) => sum + calculateDays(r.start_date, r.end_date), 0);
  const pendingDays = leaveRequests
    .filter(r => r.status === 'pending')
    .reduce((sum, r) => sum + calculateDays(r.start_date, r.end_date), 0);
  const totalAllowance = profile?.annual_holiday_allowance || 25;
  const holidaysRemaining = totalAllowance - approvedDays;

  const isEmployee = profile?.user_type === 'employee';
  const isSurveyor = profile?.user_type === 'surveyor' || (user?.roles || []).includes('surveyor') || user?.user_type === 'admin';
  const isAdmin = profile?.user_type === 'admin' || user?.roles?.includes('admin');

  const missingContractorFields = contractorProfile?.missing_fields || [];

  return (
    <div className="p-4 space-y-5" data-testid="portal-profile">
      <h1 className="text-xl font-bold text-white">Profile</h1>

      {/* Profile Card */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className="w-18 h-18 bg-[#F5A623] rounded-2xl flex items-center justify-center" style={{ width: '72px', height: '72px' }}>
              <span className="text-3xl font-bold text-[#1E2530]">
                {profile?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-white font-semibold text-xl">{profile?.name}</h2>
              <p className="text-gray-400 capitalize">{profile?.user_type?.replace('_', ' ')}</p>
              {profile?.trade && (
                <p className="text-[#F5A623] font-medium mt-1">{profile.trade}</p>
              )}
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {profile?.email && (
              <div className="flex items-center gap-3 text-gray-400 p-3 bg-[#1E2530] rounded-xl">
                <Mail className="w-5 h-5" />
                <span>{profile.email}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <PortalNotifications />
      {!isSurveyor && <MyHoursRequestsCard />}

      {/* OA-1 §3 — Contractor Business + Bank Details */}
      {isSubContractor && (
        <>
          <Card className="bg-[#2A3240] border-white/10" data-testid="contractor-business-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#F5A623]" />
                Business Details
                {contractorProfile?.profile_complete ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] ml-2">Complete</Badge>
                ) : (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] ml-2">Incomplete</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {missingContractorFields.length > 0 && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs">
                  Missing: {missingContractorFields.map(f => f.replace(/_/g, ' ')).join(', ')}
                </div>
              )}
              <div>
                <Label className="text-gray-300 text-sm">Business name</Label>
                <Input
                  value={contractorForm.business_name}
                  onChange={(e) => setContractorForm(f => ({ ...f, business_name: e.target.value }))}
                  className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                  data-testid="contractor-business-name"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Business address</Label>
                <Textarea
                  value={contractorForm.business_address}
                  onChange={(e) => setContractorForm(f => ({ ...f, business_address: e.target.value }))}
                  rows={2}
                  className="mt-1 bg-[#1E2530] border-white/20 text-white"
                  data-testid="contractor-business-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-gray-300 text-sm">UTR number</Label>
                  <Input
                    value={contractorForm.utr_number}
                    onChange={(e) => setContractorForm(f => ({ ...f, utr_number: e.target.value }))}
                    className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                    data-testid="contractor-utr"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">NI number</Label>
                  <Input
                    value={contractorForm.ni_number}
                    onChange={(e) => setContractorForm(f => ({ ...f, ni_number: e.target.value }))}
                    className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                    placeholder="AB123456C"
                    data-testid="contractor-ni"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Personal email (for invoice copy)</Label>
                <Input
                  type="email"
                  value={contractorForm.personal_email}
                  onChange={(e) => setContractorForm(f => ({ ...f, personal_email: e.target.value }))}
                  className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                  data-testid="contractor-personal-email"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Business phone (prints on invoices)</Label>
                <Input
                  value={contractorForm.business_phone}
                  onChange={(e) => setContractorForm(f => ({ ...f, business_phone: e.target.value }))}
                  className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                  data-testid="contractor-business-phone"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#2A3240] border-white/10" data-testid="contractor-bank-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Bank Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-xs">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Changing your bank details will flag your next invoice for admin review.</span>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Account name</Label>
                <Input
                  value={contractorForm.bank_account_name}
                  onChange={(e) => setContractorForm(f => ({ ...f, bank_account_name: e.target.value }))}
                  className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                  data-testid="contractor-bank-account-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-gray-300 text-sm">Sort code</Label>
                  <Input
                    value={contractorForm.bank_sort_code}
                    onChange={(e) => setContractorForm(f => ({ ...f, bank_sort_code: e.target.value }))}
                    placeholder="00-00-00"
                    className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                    data-testid="contractor-sort-code"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">Account number</Label>
                  <Input
                    value={contractorForm.bank_account_number}
                    onChange={(e) => setContractorForm(f => ({ ...f, bank_account_number: e.target.value }))}
                    placeholder="00000000"
                    className="mt-1 h-12 bg-[#1E2530] border-white/20 text-white"
                    data-testid="contractor-account-number"
                  />
                </div>
              </div>
              <Button
                onClick={saveContractorProfile}
                disabled={contractorSaving}
                className="w-full h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold mt-2"
                data-testid="save-contractor-profile-btn"
              >
                {contractorSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Save className="w-5 h-5 mr-2" /> Save Business & Bank Details</>)}
              </Button>
            </CardContent>
          </Card>

          <InvoiceSettingsCard api={api} profile={contractorProfile} onSaved={setContractorProfile} />
          <InvoiceTemplateCard api={api} profile={contractorProfile} onSaved={setContractorProfile} />
          <SavedCustomersCard api={api} />
        </>
      )}

      {/* Holiday Allowance - Only for employees */}
      {isEmployee && (
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Palmtree className="w-5 h-5 text-green-400" />
              Holiday Allowance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-4 bg-[#1E2530] rounded-xl">
                <p className="text-3xl font-bold text-white">{totalAllowance}</p>
                <p className="text-sm text-gray-500 mt-1">Total Days</p>
              </div>
              <div className="p-4 bg-[#1E2530] rounded-xl">
                <p className="text-3xl font-bold text-amber-400">{approvedDays}</p>
                <p className="text-sm text-gray-500 mt-1">Approved</p>
              </div>
              <div className="p-4 bg-[#1E2530] rounded-xl">
                <p className="text-3xl font-bold text-green-400">{holidaysRemaining}</p>
                <p className="text-sm text-gray-500 mt-1">Remaining</p>
              </div>
            </div>
            
            {pendingDays > 0 && (
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
                <p className="text-amber-400 text-sm">
                  <span className="font-bold">{pendingDays}</span> day(s) pending approval
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Availability / Leave Request Card - For all users */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            {isEmployee ? 'Request Leave' : 'Notify Availability'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-400 text-sm mb-4">
            {isEmployee 
              ? 'Submit a request for time off'
              : 'Let us know when you\'re unavailable for work'
            }
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] text-base font-semibold active:scale-[0.98] transition-transform"
            data-testid="request-leave-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {isEmployee ? 'Request Leave' : 'Add Unavailable Dates'}
          </Button>
        </CardContent>
      </Card>

      {/* Leave Requests List */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg">
            {isEmployee ? 'My Leave Requests' : 'My Unavailable Dates'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leaveRequests.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No leave requests yet</p>
          ) : (
            <div className="space-y-3">
              {leaveRequests.map(request => (
                <div 
                  key={request.id}
                  className={`p-4 rounded-xl border ${
                    request.status === 'approved' 
                      ? 'bg-green-500/5 border-green-500/20' 
                      : request.status === 'denied'
                      ? 'bg-red-500/5 border-red-500/20'
                      : 'bg-[#1E2530] border-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={`${getStatusColor(request.status)} text-sm py-1 px-3 capitalize`}>
                      {request.status}
                    </Badge>
                    <span className={`font-bold text-lg ${
                      request.status === 'denied' ? 'text-gray-500 line-through' : 'text-white'
                    }`}>
                      {calculateDays(request.start_date, request.end_date)} days
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-5 h-5" />
                    <span>
                      {format(parseISO(request.start_date), 'dd MMM')} - {format(parseISO(request.end_date), 'dd MMM yyyy')}
                    </span>
                  </div>
                  {request.reason && (
                    <p className="text-gray-500 text-sm mt-2">{request.reason}</p>
                  )}
                  {request.admin_notes && (
                    <p className="text-amber-400 text-sm mt-3 italic bg-amber-500/10 p-2 rounded-lg">Note: {request.admin_notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Leave Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-xl">
              <Calendar className="w-6 h-6 text-blue-400" />
              {isEmployee ? 'Request Leave' : 'Add Unavailable Dates'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {isEmployee 
                ? 'Submit a holiday or time off request'
                : 'Let us know when you\'re not available for work'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300 text-base">Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  className="h-14 bg-[#1E2530] border-white/20 text-white text-base"
                  data-testid="leave-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 text-base">End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  min={formData.start_date || format(new Date(), 'yyyy-MM-dd')}
                  className="h-14 bg-[#1E2530] border-white/20 text-white text-base"
                  data-testid="leave-end-date"
                />
              </div>
            </div>

            {formData.start_date && formData.end_date && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-center">
                <p className="text-blue-400">
                  <span className="font-bold text-2xl">
                    {calculateDays(formData.start_date, formData.end_date)}
                  </span> day(s) {isEmployee ? 'requested' : 'unavailable'}
                </p>
                {isEmployee && (
                  <p className="text-blue-300 text-sm mt-1">
                    You have {holidaysRemaining} days remaining
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-gray-300 text-base">Reason (optional)</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Holiday, personal, etc."
                rows={2}
                className="bg-[#1E2530] border-white/20 text-white resize-none text-base"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDialogOpen(false);
                  setFormData({ start_date: '', end_date: '', reason: '' });
                }}
                className="flex-1 h-14 border-white/20 text-gray-300 text-base active:scale-[0.98] transition-transform"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitRequest}
                disabled={submitting || !formData.start_date || !formData.end_date}
                className="flex-1 h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] text-base font-semibold active:scale-[0.98] transition-transform"
                data-testid="submit-leave-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Display Preferences (admin only) */}
      {isAdmin && (
        <Card className="bg-[#2A3240] border-white/10 mt-6">
          <CardContent className="p-5">
            <h3 className="text-white font-semibold mb-3">Display Preferences</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Current view: <span className="text-white">Portal View</span></p>
                <p className="text-gray-500 text-xs mt-0.5">Admin users can switch between Portal and Admin views</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  try { localStorage.removeItem('lux_view_preference'); } catch { /* ignore */ } // eslint-disable-line no-empty
                  toast.success('View preference reset to auto-detect');
                }}
                className="border-white/20 text-gray-300 hover:bg-white/5 text-xs"
                data-testid="reset-view-pref"
              >
                Reset to auto-detect
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
